import { ENTITLEMENT_CATEGORY_ID } from '../../../guest/entitlements/constants';
import {API_BASE_URL, CONFIG_BASE_URL, Users_BASE_URL} from '../../windowProps';

// Docs: https://alterradev.ikonpass.com/ResortAPI/documentation/index.html


/**
 * Endpoint to hit to fetch the list of FAQs
 */
export const API_APPLIANCE_VERSION =`${process.env.PUBLIC_URL}/appliance-version.json`;


/**
 * The API endpoint to hit to get the app configuration settings for the site we're at
 * Using local for testing
 */
export const API_APP_CONFIG = (resortId) => `${CONFIG_BASE_URL}/Config/${resortId}`;


/**
 * The API endpoint to hit to get information about all of the Ikon resorts, including which resort we're at
 */
export const API_RESORT_DETAILS = `${API_BASE_URL}/resorts`;


/**
 * The API endpoint to hit when getting a guest photo. Takes a query param: `?url=<Unescaped Cloudinary URL>`
 */
export const API_GUEST_PHOTOS = `${API_BASE_URL}/guests/photos`;


/**
 * The API endpoint to hit when printing a pass to get PDF for pass label.
 */
export const API_MEDIA_REISSUE = `${API_BASE_URL}/guests/media/reissue`;

/**
 * The API endpoint to hit to determine the number of pass reprints/reissues for a season for a guest or set of guests
 */
export const API_SEASON_REPRINT_COUNT = (reprintStartDate: string, guestIds: string) => `${API_BASE_URL}/guests/media/reissue/seasonCount/${reprintStartDate}/${guestIds}`;


/**
 * The endpoint to hit to redeem an entitlements (currently only Direct to Window and Friends and Family)
 */
export const API_REDEEM_ENTITLEMENT = `${API_BASE_URL}/entitlements/uses/central`;

/**
 * The API endpoint for mass corrective redemptions
 */
export const API_MASS_CORRECTION = `${API_BASE_URL}/entitlements/uses/bulk`;

/**
 * The API endpoint for pass usage migration
 */
export const API_USAGE_MIGRATION = `${API_BASE_URL}/entitlements/uses/migrate`;

/**
 * POST to this endpoint with the guestId you want to import, and you'll trigger a guest record import from central to
 * the resort's local system for the associated guest.
 */
export const API_IMPORT_GUEST = `${API_BASE_URL}/guests`;


/**
 * GET from this endpoint for the full list of hotlist reasons (and reason 0: not on hotlist)
 */
export const API_HOTLIST_REASONS = `${API_BASE_URL}/guests/hotlists/reasons`;


/**
 * GET from this endpoint for the full list of entitlement hotlist statuses
 * (e.g. active (is on hotlist), inactive (is not on hotlist))
 */
export const API_ENTITLEMENT_HOTLIST_STATUSES = `${API_BASE_URL}/hotlists/statuses`;


/**
 * GET from this endpoint for the full list of entitlement hotlist types
 * (e.g. lost pass, fradulent use)
 */
export const API_ENTITLEMENT_HOTLIST_TYPES = `${API_BASE_URL}/hotlists/types`;


/**
 * PUT to this endpoint to remove an entitlement from a hotlist
 * Required parameters:
 *   masterguestid:        The ID of the mast guest associated with the entitlement
 *   entitlementhotlistid: ID of the hotlist entry being removed
 *   commenttext:          String explaining why hotlist is being removed
 *   resort:               Abbreviation for the resort this hotlist applies to (or ALL for global)
 *   modifiedbyid:         ID of the Ikon Pass user making the removal
 */
export const API_ENTITLEMENT_HOTLIST_REMOVE = (requestingResort) => `${API_BASE_URL}/hotlists?requestingResortAbbrv=${requestingResort}`;


/**
 * Endpoint to check the availability of a username
 * @param userName
 */
export const checkUserNameAvailable = (userName) => `${Users_BASE_URL}/users/isAvailable/${userName}`;

/**
 * Endpoint to check the availability of an email
 * @param email
 */
export const checkEmailAvailable = (email) => `${Users_BASE_URL}/users/emailIsAvailable/${email}`;

/**
 * Endpoint to check the availability of a username
 * @param userName
 */
export const apiGetSuggestedUsername = (firstName, lastName) => `${Users_BASE_URL}/users/suggestedUsername/${firstName}/${lastName}`;

/**
 * Endpoint to get all Entitlement Type IDs
 */
export const API_GET_ENTITLEMENT_TYPES = () => `${API_BASE_URL}/entitlements/entitlementtypes`;

/**
 * Endpoint to get all Selling Seasons
 */
export const apiGetSellingSeasons = () => `${API_BASE_URL}/entitlements/sellingseasons`;

/**
 * Endpoint to get all Fulfillment Seasons
 */
export const apiGetFulfillmentSeasons = () => `${API_BASE_URL}/entitlements/fulfillmentseasons`;

/**
 * Endpoint to get all editable effective date range access rules for a resort (validation configuration tool)
 */
export const apiGetEditableEffectiveDateRangeAccessRules = (resortAbbreviation: string) => `${API_BASE_URL}/liftaccess/${resortAbbreviation}/editable-effective-date-ranges`;

/**
 * Endpoint to search Lift Access Rules based on LiftAccessRuleFilter
 */
export const apiSearchLiftAccessRules = () => `${API_BASE_URL}/liftaccess/search`;

/**
 * Endpoint to get the valid lift access locations for a set of entitlement types at a resort. The API
 * accepts a repeated `entitlementTypeIds` query param and returns one entry per requested entitlement type.
 */
export const apiGetValidLiftAccessLocations = (resortAbbreviation: string, entitlementTypeIds: number[]) =>
  `${API_BASE_URL}/liftaccess/${resortAbbreviation}/valid-locations?${entitlementTypeIds.map(id => `entitlementTypeIds=${id}`).join('&')}`;

/**
 * Endpoint to Save a set of lift access rule changes that can include adds, updates and deletes for a 
 * single entitlement context based on EntitlementTypeId, FulfillmentSeasonId, LiftAccessRuleScopeCategoryId,
 * and ScopeEntityId (ResortId, LocationId, ResortGroupId)
 */
export const apiSaveLiftAccessRules = () => `${API_BASE_URL}/liftaccess/save-changes`;

/**
 * Endpoint to Bulk Add a set of lift access rule changes for one or more 
 * entitlement contexts based on EntitlementTypeId, FulfillmentSeasonId, LiftAccessRuleScopeCategoryId,
 * and ScopeEntityId (ResortId, LocationId, ResortGroupId)
 */
export const apiBulkAddLiftAccessRules = () => `${API_BASE_URL}/liftaccess/bulk-add-access-rules`;

/**
 * Endpoint to delete all access rules for a list of entitlement contexts based on EntitlementTypeId,
 * FulfillmentSeasonId, LiftAccessRuleScopeCategoryId, and ScopeEntityId (ResortId, LocationId, ResortGroupId).
 * Returns the outcome per context, so a bad context is skipped rather than rolling back the
 * request. Callers chunk large selections: one call is one transaction. A single rule set
 * delete sends exactly one context.
 */
export const apiBulkDeleteLiftAccessRules = () => `${API_BASE_URL}/liftaccess/bulk-delete-all-for-contexts`;

/**
 * Endpoint to get friends and family entitlement types blackout dates metadata
 */
export const apiGetFnFBlackoutDatesMetadata = (resortAbbreviation) => `${API_BASE_URL}/entitlements/entitlementtypes/metadata/blackoutdates?resortAbbrev=${resortAbbreviation}&categoryId=${ENTITLEMENT_CATEGORY_ID.FNF_SHARED_VOUCHER}`;

/**
 * Endpoint to get lift entitlement types blackout dates metadata
 */
export const apiGetLiftBlackoutDatesMetadata = (resortAbbreviation) => `${API_BASE_URL}/entitlements/entitlementtypes/metadata/blackoutdates?resortAbbrev=${resortAbbreviation}&categoryId=${ENTITLEMENT_CATEGORY_ID.LIFT}`;

/**
 * Endpoint to get Effective Date Range Lift Access Rules Change History
 */
export const apiGetEffectiveDateRangeRuleHistory = (resortAbbreviation: string, numberOfResults: number, fulfillmentSeasonIdList: string) => 
    `${API_BASE_URL}/liftaccesshistory/effective-date-range/?resortAbbreviation=${resortAbbreviation}&numberOfResults=${numberOfResults}${fulfillmentSeasonIdList}`;

/** Endpoint to save changes to Effective Date Range Lift Access Rules from the validation configuration tool */
export const apiUpdateEffectiveDateRangeRules = () => `${API_BASE_URL}/liftaccess/update-effective-date-ranges`;

/**
 * Endpoint to get all entitlement redemption type descriptions
 */
export const API_GET_ENTITLEMENT_REDEMPTION_TYPES = () => `${API_BASE_URL}/entitlements/redemptions/type`;

/**
 * Endpoint to get all entitlement shares
 */
export const apiGetEntitlementShares = (guestId: string) => `${API_BASE_URL}/guests/${guestId}/entitlementshares`;

/**
 * The API endpoint to hit when searching for customers.
 */
export const apiGuestSearch = (query: string, userId: string, resortId: number, searchDateTime: string) => 
  `${API_BASE_URL}/guests/search/${query}?userId=${userId}&resortId=${resortId}&searchDateTime=${searchDateTime}`;

/**
 * The API endpoint to get user search history
 */
export const apiSearchHistory = (userId: string, resortId: number) => `${API_BASE_URL}/guests/${userId}/${resortId}/searchHistory`;

/**
 * The API endpoint to clear a users search history
 */
export const clearSearchHistory = (userId: string, resortId: number) => `${API_BASE_URL}/guests/${userId}/${resortId}/clearSearchHistory`;

/**
 * The API endpoint to hit to get a specific guest's details.
 */
export const apiGuestDetails = (guestId: string) => `${API_BASE_URL}/guests/${guestId}`;

/**
 * The API endpoint to hit to get a specific guest's name info and photo
 */
export const apiPaymentGuest = (guestId: string) => `${API_BASE_URL}/guests/${guestId}/nameAndPhoto`;


/**
 * Function accepting a guest ID and returning the endpoint to hit for getting that guest's household.
 * I wish this were a static constant, but that's just not how the endpoint was written.
 * @param guestId - The ID of the guest whose household you want
 */
export const apiGetHousehold = (guestId: string) => `${API_BASE_URL}/guests/${guestId}/household`;

/**
 * The API endpoint to get a specific guest's payment registrations
 */
export const apiGetGuestPaymentRegistrations = (guestId: string) => `${API_BASE_URL}/payments/registration/${guestId}`;

/**
 * The API endpoint to get details for a payment method and its active registrations
 */
export const apiGetPaymentMethod = (paymentMethodId: number) => `${API_BASE_URL}/payments/method/${paymentMethodId}`;

/**
 * The API endpoint to deactivate a guest payment registration
 */
export const apiDeactivateGuestPaymentRegistration = (paymentRegistrationId: number) => `${API_BASE_URL}/payments/registration?paymentRegistrationId=${paymentRegistrationId}`;

/**
 * The endpoint to hit if you want to get a guest's entitlements which is just a list of stuff they're entitled to
 * including their pass type.
 * @param guestId the ID of the guest whose entitlements you want.
 * @param requestingResortAbbrev
 */
export const apiGetEntitlements = (
  guestId: string,
  requestingResortAbbrev: string,
) => `${API_BASE_URL}/guests/${guestId}/entitlements?requestingResortAbbrev=${requestingResortAbbrev}&includeDisplayOnly=true`;


/**
 * The endpoint to POST new guest photos to
 */
export const apiSetGuestPhoto = (guestId: string, userId: string, resortAbbreviation: string) => `${API_BASE_URL}/guests/${guestId}/photos/central?userId=${userId}&resortAbbreviation=${resortAbbreviation}`;


/**
 * The API endpoint to hit when associating a guest with the RFID code in their newly-printed pass.
 */
export const apiEncodeRfid = (mediaId: string) => `${API_BASE_URL}/guests/media/${mediaId}/encode`;


/**
 * The API endpoint to hit when adding a waiver.
 */
export const apiSubmitWaiver = (guestId: string) => `${API_BASE_URL}/guests/${guestId}/waivers/central`;

/**
 * The API endpoint to hit when getting waiver lookup.
 */
export const apiGetWaiverLookup = () => `${API_BASE_URL}/waiverversiontypes`;



/**
 * The endpoint to hit if you want the redemption entitlements of a guest by category type.
 * @param guestId the ID of the guest whose redemption entitlements you want.
 * @param categoryId the ID of the category of the entitlements type.  ID represents either Friends and Family or Direct
 * @param isDisplayOnly boolean to determine if the entitlements are display only or not
 * to Window
 * @param requestingResortAbbrev
 */
export const apiGetRedemptionEntitlements = (
  guestId: string,
  categoryId: number,
  isDisplayOnly: boolean,
  requestingResortAbbrev: string,
  includeAllResortRedemptions : boolean,
) => `${API_BASE_URL}/guests/${guestId}/entitlements/redemptions/${categoryId}?requestingResortAbbrev=${requestingResortAbbrev}&isDisplayOnly=${isDisplayOnly}&includeAllResortRedemptions=${includeAllResortRedemptions}`;

/**
 * The endpoint to hit if you want the redemption entitlements of a guest by category type.
 * @param guestId the ID of the guest whose redemption entitlements you want.
 * @param includeDisplayOnly
 * @param requestingResortAbbrev
 */
export const apiGetRedemptionHistory = (
  guestId: string,
  includeDisplayOnly: boolean,
  requestingResortAbbrev: string,
) => `${API_BASE_URL}/guests/${guestId}/entitlements/redemptions/history?requestingResortAbbrev=${requestingResortAbbrev}&includeDisplayOnly=${includeDisplayOnly}`;


/**
 * Docs:
 * https://alterradev.ikonpass.com/ResortAPI/documentation/index.html#/Guest/V1GuestsByIdEntitlementsRedemptionsByCategoryIdGet
 *
 * The endpoint to hit if you want to get a guest's resort history at a resort with the resortAbbrev using the pass
 * with the provided entitlementId.
 * @param guestId the ID of the guest whose resort history you want.
 * @param categoryId the ID of the category of the entitlements type.  ID represents either Friends and Family or Direct
 * to Window.
 * @param resortAbbrev the ID of the resort whose history you want.
 * @param isDisplayOnly
 * @param requestingResortAbbrev
 */
export const apiGetResortHistory = (
  guestId: string,
  categoryId: number,
  resortAbbrev: string,
  isDisplayOnly: boolean,
  requestingResortAbbrev: string,
) => `${API_BASE_URL}/guests/${guestId}/entitlements/redemptions/${categoryId}?requestingResortAbbrev=${requestingResortAbbrev}&requestedResortAbbrev=${resortAbbrev}&isDisplayOnly=${isDisplayOnly}`;
/**
 * The API endpoint to hit to get a guest's media card history -- the history of their printed passes
 */
export const apiGetMediaHistories = (guestId: string) => `${API_BASE_URL}/guests/${guestId}/media`;

/**
 * The API endpoint to hit to disable a guest's pass in the event of lost, stolen, etc
 */
export const apiDisableMedia = `${API_BASE_URL}/guests/media/disable`;

export const apiEnableMedia = `${API_BASE_URL}/guests/media/enable`;


/**
 * The API endpoint to get the list of comments agents have left on a particular guest. POSTing to this endpoint will
 * add new comments.
 */
export const apiGuestComments = (guestId: string) => `${API_BASE_URL}/guests/${guestId}/comments`;


/**
 * The API endpoint to POST to to add/remove a guest to the hotlist (blacklist their passes)
 * @param guestId - the ID of the guest who you want to add/remove to the hotlist.
 */
export const apiHotlist = (guestId: string) => `${API_BASE_URL}/guests/${guestId}/hotlists`;


/**
 * The API endpoint to POST to, to add a guest's entitlement to a global or resort-specific
 * hotlist (i.e., blacklist their passes at one or all Alterra resorts)
 */
export const apiAddToHotlist = (resortAbbrev) => `${API_BASE_URL}/hotlists?requestingResortAbbrv=${resortAbbrev}`;


/**
 * The API endpoint to GET  to get entitlement hotlist (blacklist their passes) info
 * @param entitlementId - the ID of the entitlment whose hotlist you require
 */
export const apiEntitlementHotlist = (entitlementId: number) => `${API_BASE_URL}/hotlists/${entitlementId}`;


/**
 * The API endpoint to GET Entitlements for Resort Entitlement Mapping Tool
 * @param resortAbbrev
 * @param includeOld
 */
export const apiGetResortMapping = (
  includeOld: boolean,
  resortAbbrev: string,
  resortConfigurable: boolean,
) => `${API_BASE_URL}/mappings?shouldIncludeOldMappings=${includeOld}&resortAbbrev=${resortAbbrev}&resortConfigurable=${resortConfigurable}`
// https://dta.alterramtnco.com/mappings/api/mappings?resortAbbrev=SB&shouldIncludeOldMappings=true

/**
 * The API endpoint to GET Entitlements WITH ERRORS for Resort Entitlement Mapping Tool
 * @param resortAbbrev 
 * @returns 
 */
export const apiGetResortMappingErrors = (
  resortAbbrev: string,
) => `${API_BASE_URL}/mappings/mappingerrors?resortAbbrev=${resortAbbrev}`;

export const apiGetResortMappingHasError = (
  resortAbbrev: string,
) => `${API_BASE_URL}/mappings/mappingerror?resortAbbrev=${resortAbbrev}`;

/**
 * The API endpoint to PUT Resort Mappings to
 */
export const updateResortMapping = (resortAbbrev) => `${API_BASE_URL}/mappings?resortAbbrev=${resortAbbrev}`;

/**
 * The API endpoint to GET Resort Mapping Errors for a specific RTP mapping
 */

export const getProductMappingErrors = (resortAbbrev : string, productHeaderCode : string | null) => `${API_BASE_URL}/mappings/mappingerrors?resortAbbrev=${resortAbbrev}&productHeaderCode=${productHeaderCode}`;

/**
 * The API endpoint to GET Resort Mapping Errors for a specific Siriusware mapping
 */
export const getProductMappingErrorSW = (resortAbbrev : string, department: string, item: string, category: string) => `${API_BASE_URL}/mappings/swmappingerrors?resortAbbrev=${resortAbbrev}&department=${department}&item=${item}&category=${category}`;

/**
 * The API endpoint to GET Product Hydration Errors for a specific Resort
 */

export const apiGetProductHydrationErrors = (resortAbbrev : string) => `${API_BASE_URL}/getproducthydrationerrors?resortAbbrev=${resortAbbrev}`;

/**
 * API endpoint to retry product hydration errors
 * @param resortAbbrev 
 * @param entitlementTypeId 
 * @param fulfillmentSeasonId 
 * @param ageBin 
 * @returns 
 */
export const apiRetryProductHydrationErrors = (resortAbbrev : string, entitlementTypeId: number | null, fulfillmentSeasonId?: number | null, ageBin?: string) => `${API_BASE_URL}/retryproducthydrationerrors?resortAbbrev=${resortAbbrev}&entitlementTypeId=${entitlementTypeId}&fulfillmentSeasonId=${fulfillmentSeasonId}&ageBin=${ageBin ? encodeURIComponent(ageBin) : ''}`;

/**
 * Endpoint to retrieve guest hydration errors
 * @param resortAbbrev 
 * @param pageSize 
 * @param lastCreateUtcDateTime 
 * @returns 
 */
export const apiGetGuestHydrationErrors = (resortAbbrev : string, days: number, pageSize?: number | null, pageNumber?: number | null, masterGuestId?: string | null) => `${API_BASE_URL}/reports/guesthydrationerrors/${resortAbbrev}?days=${days}&pageSize=${pageSize}&pageNumber=${pageNumber}`+(masterGuestId != null ? `&masterguestId=${masterGuestId}` : '');

/**
 * API Endpoint to replay guest hydration errors 
 * @param resortAbbrev 
 * @param masterGuestId 
 * @returns 
 */
export const apiReplayGuestHydrationErrors = (resortAbbrev : string, masterGuestId : string) => `${API_BASE_URL}/replayguesthydrationerrors/${resortAbbrev}/${masterGuestId}`;

/**
 * Endpoint to retrieve guest hydration errors count
 * @param resortAbbrev 
 * @returns 
 */
export const apiGetGuestHydrationErrorsCount = (resortAbbrev : string) => `${API_BASE_URL}/reports/guesthydrationerrorcount/${resortAbbrev}`;


export const apiDownloadHotlistReport = (
  resortAbbrev : string, 
  fromDate : string, 
  toDate: string, 
  hotlistTypeId: number | null) => `${API_BASE_URL}/reports/hotlistreport/${resortAbbrev}?fromDate=${fromDate}&toDate=${toDate}&hotlistType=${hotlistTypeId}`;

/**
 * Endpoint to retrieve weekly visitor reports for specified resort
 * @param resortAbbrev 
 * @param numberOfFiles 
 * @returns 
 */
export const apiGetWeeklyVisitorReports = (resortAbbrev: string, numberOfFiles: number) => `${API_BASE_URL}/reports/weeklyAccessDetail/${resortAbbrev}?numFiles=${numberOfFiles}`;

/**
 * API ednpoint to retrieve dtl script versions
 * @param resortAbbrev 
 * @returns 
 */
export const apiGetScriptVersions = (resortAbbrev : string) => `${API_BASE_URL}/getdtlversions?resortAbbrev=${resortAbbrev}`;

/**
 * Endpoint to hit to get the user's profile information on login.
 */
export const API_GET_USER = (userId: string) =>`${Users_BASE_URL}/Users/${userId}`;

/**
 * Endpoint to hit to get all Resort Tools Users
 */
export const API_GET_USERS_BY_RESORT = (resortId) =>  `${Users_BASE_URL}/Users/resort/${resortId}`;

/**
 * Endpoint to hit to create a new Resort Tools user
 */
export const API_ADD_USER = (createdByUserId: string, createdByResortId: number) => `${Users_BASE_URL}/Users/${createdByUserId}/${createdByResortId}`;


/**
 * Endpoint to hit to delete a Resort Tools user
 * @param identityEndpoint - The location of Identity Server on the network
 * @param userName - The user's username
 */
export const apiRemoveUser = (
  deletedByUserId: string, deletedByResortId: number, userId: string, 
) => `${Users_BASE_URL}/Users/${deletedByUserId}/${deletedByResortId}/${userId}`;

export const apiBlockUser = (disabledByUserId: string, disabledByResortId: number, userId: string) => `${Users_BASE_URL}/Users/disable/${disabledByUserId}/${disabledByResortId}/${userId}`;

export const apiUnblockUser = (enabledByUserId: string, enabledByResortId: number, userId: string) => `${Users_BASE_URL}/Users/enable/${enabledByUserId}/${enabledByResortId}/${userId}`;


/**
 * Endpoint to hit to update a specific Resort Tools users email, role, resort locations
 * first name and last name. (does not work to change username)
 * @param userId - The user's auth0 id
 */
export const apiUpdateUser = (updatedByUserId: string, updatedByResortId: number, 
  userId: string,
) => `${Users_BASE_URL}/Users/${updatedByUserId}/${updatedByResortId}/${userId}`;


/**
 * Endpoint to hit to update a specific Resort Tools users email, role, resort locations
 * first name and last name. (does not work to change username)
 * @param userId - The user's auth0 id
 */
export const apiUpdateUserLastResort = (
  userId: string,
  resort: number
) => `${Users_BASE_URL}/Users/${userId}/resort/${resort}`;

/**
 * Endpoint to update a specific Resort Tools user's language/date preferences
 * @param userId - the user's auth0id
 */
export const apiUpdateUserPreference = (
  userId: string,
  languageCode: string,
  dateCode: string,
) => `${Users_BASE_URL}/Users/${userId}/preferences/${languageCode}/${dateCode}`;

/**
 * Enpoint to change a users password
 * Body contains new password
 */
export const apiChangeUserPassword = (changedByUserId: string, changedByResortId: number, userId,newPwd) => `${Users_BASE_URL}/Users/${changedByUserId}/${changedByResortId}/${userId}/changePassword?newPassword=${newPwd}`;

// Release Notes and FAQ

export const apiGetReleaseNotes = `${CONFIG_BASE_URL}/Config/releaseNotes`;

/**
 * Endpoint to hit to fetch the list of FAQs
 */
export const apiGetFaqs =`${CONFIG_BASE_URL}/Config/faq`;

// Help and Training

/**
 * Endpoint to fetch the list of training document metadata
 */
export const apiGetTrainingDocuments = `${API_BASE_URL}/training/documents`;

/**
 * Endpoint to fetch a training document's file bytes (rendered in-app by docx-preview)
 * @param documentId
 */
export const apiGetTrainingDocumentContent = (documentId: number | string) => `${API_BASE_URL}/training/documents/${documentId}/content`;

/**
 * Endpoint for a single training document — replace via PUT, delete via DELETE
 * @param documentId
 */
export const apiTrainingDocumentById = (documentId: number | string) => `${API_BASE_URL}/training/documents/${documentId}`;
